import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Compass, Clock, Trophy, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function AthleteHome() {
  const { user } = useAuth();
  const isVerified = user?.athlete_verification_status === "approved";
  const verificationStatus = user?.athlete_verification_status || "none";
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/bookings").then((r) => setRecent((r.data.items || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <section className="relative rounded-3xl overflow-hidden border border-border">
        <img
          src="https://images.pexels.com/photos/9899398/pexels-photo-9899398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Court"
          className="w-full h-40 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
        <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
          <p className="text-xs uppercase tracking-widest opacity-80">Ready to practice?</p>
          <h2 className="text-2xl bb-display font-semibold">Hey {user?.name?.split(" ")[0]}</h2>
          <p className="text-sm opacity-90">Book a ball boy near you in minutes.</p>
        </div>
      </section>

      {isVerified ? (
        <Link to="/request" data-testid="athlete-request-ballboy-cta">
          <Button className="w-full h-14 text-base" size="lg">
            Request a ball boy <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      ) : (
        <Card className="border-[hsl(var(--warning)/0.5)] bg-[hsl(var(--warning)/0.08)]" data-testid="athlete-verify-required-banner">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="grid place-items-center h-11 w-11 rounded-full bg-[hsl(var(--warning)/0.18)] text-[hsl(var(--warning))] shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold bb-display">Verify your identity to start booking</div>
                <p className="text-sm text-muted-foreground mt-1">
                  We use AWS Rekognition to instantly match your government ID with a selfie. This keeps the BallBoy community safe — and your account future-proof.
                </p>
                {verificationStatus === "pending" && (
                  <p className="text-xs text-[hsl(var(--warning))] mt-2">
                    Your previous submission is under admin review.
                  </p>
                )}
                {verificationStatus === "rejected" && (
                  <p className="text-xs text-[hsl(var(--destructive))] mt-2">
                    Your previous submission was rejected. Please retry with clearer photos.
                  </p>
                )}
              </div>
            </div>
            <Link to="/athlete/verify" data-testid="athlete-verify-now-cta">
              <Button className="w-full h-12">
                <ShieldCheck className="h-4 w-4 mr-2" /> Verify identity now
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <Compass className="mx-auto h-5 w-5 text-primary" />
            <div className="text-xs text-muted-foreground mt-1">Any court</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <Clock className="mx-auto h-5 w-5 text-primary" />
            <div className="text-xs text-muted-foreground mt-1">1–3 hours</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <Trophy className="mx-auto h-5 w-5 text-primary" />
            <div className="text-xs text-muted-foreground mt-1">Top-rated</div>
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Recent bookings</h3>
          <Link to="/bookings" className="text-xs text-primary" data-testid="athlete-view-all-bookings">
            View all
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No bookings yet. Tap “Request a ball boy” to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map((b) => (
              <Link
                key={b.id}
                to={`/bookings/${b.id}`}
                className="block rounded-xl border border-border bg-card p-3 hover:bg-muted/60"
                data-testid="recent-booking-link"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm font-medium capitalize">{b.sport}</div>
                    <div className="text-xs text-muted-foreground">{new Date(b.start_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums">${b.price_total?.toFixed?.(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
